import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import { PageHeader } from "../components/PageHeader";
import { Toolbar } from "../components/Toolbar";
import { displayContainerId, displayEnvironment } from "../lib/languages";
import { seedCloudKitSchema, type SeedSchemaResult } from "../lib/seedApi";
import { LITURGY_SCHEMA_VERSION, type HealthResponse } from "../lib/types";

export function OverviewPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [seedResult, setSeedResult] = useState<SeedSchemaResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/health");
        if (!res.ok) throw new Error(`Health check failed (${res.status})`);
        const data = (await res.json()) as HealthResponse;
        if (!cancelled) setHealth(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not reach /api/health");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const envLabel = health?.environment ?? displayEnvironment();
  const container = health?.containerId ?? displayContainerId();
  const cloudKitOk = health?.cloudKitConfigured ?? false;
  const appleOk = health?.appleAuthConfigured ?? false;

  const checklist = [
    {
      ok: Boolean(container),
      label: "CloudKit container documented",
      detail: container,
    },
    {
      ok: appleOk,
      label: "Sign in with Apple configured",
      detail: "APPLE_CLIENT_ID, SESSION_SECRET, and admin allowlist",
    },
    {
      ok: cloudKitOk,
      label: "CloudKit server-to-server key",
      detail: "CLOUDKIT_KEY_ID and CLOUDKIT_PRIVATE_KEY",
    },
    {
      ok: Boolean(seedResult),
      label: "Schema seed written",
      detail: seedResult
        ? `${seedResult.written} records · types: ${seedResult.recordTypes.join(", ")}`
        : "Import gospelgo-schema-v1.ckdb in Dashboard, then Seed below",
    },
  ];

  async function onSeed() {
    setSeedBusy(true);
    setSeedError(null);
    try {
      const result = await seedCloudKitSchema();
      setSeedResult(result);
    } catch (err) {
      setSeedError(err instanceof Error ? err.message : "Seed failed.");
    } finally {
      setSeedBusy(false);
    }
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow="Health"
        title="Overview"
        description="Environment status, schema bootstrap, and shortcuts into calendar and scrape."
        actions={
          <Toolbar>
            <Link className="btn btn-quiet btn-sm" to="/readings">
              Calendar
            </Link>
            <Link className="btn btn-primary btn-sm" to="/scrape">
              Scrape
            </Link>
          </Toolbar>
        }
      />

      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
          {error}. Run <code>npm run pages:dev</code> so Functions are available.
        </div>
      )}

      <div className="grid-stats">
        <div className="panel stat">
          <div className="label">Environment</div>
          <div className="value" style={{ fontSize: 22 }}>
            {envLabel === "production" ? "Prod" : "Dev"}
          </div>
          <div className="hint">{envLabel}</div>
        </div>
        <div className="panel stat">
          <div className="label">Schema</div>
          <div className="value">v{health?.schemaVersion ?? LITURGY_SCHEMA_VERSION}</div>
          <div className="hint">
            <Link className="link-inline" to="/schema">
              Open schema
            </Link>
          </div>
        </div>
        <div className="panel stat">
          <div className="label">Reading packs</div>
          <div className="value">—</div>
          <div className="hint">
            <Link className="link-inline" to="/readings">
              Calendar
            </Link>
          </div>
        </div>
        <div className="panel stat">
          <div className="label">Drafts pending</div>
          <div className="value">—</div>
          <div className="hint">
            <Link className="link-inline" to="/publish">
              Review queue
            </Link>
          </div>
        </div>
      </div>

      <div className="panel panel-pad stack" style={{ marginBottom: 16 }}>
        <div className="section-title">
          <h2>Setup checklist</h2>
          <p>Confirm the container and bootstrap Development schema before production promote.</p>
        </div>
        <ul className="checklist">
          {checklist.map((item) => (
            <li key={item.label}>
              <span
                className={`mark ${item.ok ? "ok" : "miss"}`}
                aria-label={item.ok ? "Complete" : "Incomplete"}
              >
                {item.ok ? "✓" : "!"}
              </span>
              <div>
                <strong>{item.label}</strong>
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                  {item.detail}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="panel panel-pad stack" style={{ marginBottom: 16 }}>
        <div className="section-title">
          <h2>Bootstrap Development schema</h2>
          <p>
            Download <code>gospelgo-schema-v1.ckdb</code>, import it in Dashboard (Development), then
            seed sample records.
          </p>
        </div>

        <Toolbar>
          <a
            className="btn btn-primary"
            href="/catalog/gospelgo-schema-v1.ckdb"
            download="gospelgo-schema-v1.ckdb"
          >
            Download .ckdb
          </a>
          <button
            type="button"
            className="btn btn-quiet"
            disabled={seedBusy || !cloudKitOk}
            onClick={() => void onSeed()}
          >
            {seedBusy ? "Seeding…" : "Seed sample records"}
          </button>
          <Link className="btn btn-quiet" to="/schema">
            Schema docs
          </Link>
        </Toolbar>

        {!cloudKitOk && (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            CloudKit keys are not configured on this Worker yet.
          </p>
        )}

        {seedError && (
          <div className="error-banner" role="alert">
            {seedError}
          </div>
        )}

        {seedResult && (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              Wrote <strong>{seedResult.written}</strong> records in{" "}
              <code>{seedResult.environment}</code>. Sample day:{" "}
              <Link className="link-inline" to={`/readings/${seedResult.seedDateKey}`}>
                {seedResult.seedDateKey}
              </Link>
            </p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Types: {seedResult.recordTypes.join(", ")}
            </p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {seedResult.note}
            </p>
          </div>
        )}
      </div>

      <div className="panel panel-pad stack">
        <div className="section-title">
          <h2>Roadmap</h2>
          <p>Firestore sync is live (C5); prod promote UX is next (C6).</p>
        </div>
        <div className="roadmap-list">
          <RoadmapRow to="/readings" icon="calendar" title="Calendar" phase="Live" />
          <RoadmapRow to="/scrape" icon="download" title="Scrape jobs" phase="Live · EN + TL" />
          <RoadmapRow to="/publish" icon="cloud" title="Review & publish" phase="Live · C4" />
          <RoadmapRow to="/sync" icon="sync" title="Firestore sync" phase="Live · C5" />
        </div>
      </div>
    </AppShell>
  );
}

function RoadmapRow({
  to,
  icon,
  title,
  phase,
}: {
  to: string;
  icon: "calendar" | "download" | "cloud" | "sync";
  title: string;
  phase: string;
}) {
  return (
    <Link to={to} className="roadmap-row">
      <Icon name={icon} size={16} />
      <span>{title}</span>
      <span className="chip neutral">{phase}</span>
    </Link>
  );
}
